function isRealName(s) {
  if (!s) return false;
  const lower = s.toLowerCase();
  return lower !== 'unknown' && lower !== 'n/a' && lower !== 'na';
}

/**
 * Unified display label for provider lookups (dropdowns, lists).
 * Rule: [Name] - [type code] (ASCII hyphen; type omitted if blank)
 * - Name: "Last, First" when first/last look real (not "Unknown", "N/A", etc.); else payee_name; else fallback.
 *   So org-style providers (e.g. AT vendors with no person name) show the payee/business name (e.g. "Hanger - AT").
 * - Type: provider_type_code (e.g. DR, AT, ST) appended as " - CODE" when present.
 *
 * @param {Object} provider - Provider object from API (first_name, last_name, payee_name, provider_type_code, id).
 * @returns {string} Display label for dropdown option or list cell.
 */
export function formatProviderDisplayLabel(provider) {
  if (!provider) return '—';
  const first = (provider.first_name ?? '').trim();
  const last = (provider.last_name ?? '').trim();
  const payee = (provider.payee_name ?? '').trim();
  const code = (provider.provider_type_code ?? '').trim();

  const hasRealName = isRealName(last) || isRealName(first);
  let name;
  if (hasRealName) {
    name = [last, first].filter(Boolean).join(', ');
  } else if (payee) {
    name = payee;
  } else {
    const pid = provider.id ?? provider.provider_id;
    name = pid != null && pid !== '' ? `Provider #${pid}` : 'Provider';
  }

  const typePart = code ? ` - ${code}` : '';
  return `${name}${typePart}`;
}

/**
 * Site suffix for provider+office: "City · Street" only (no state), or city/street/label fallbacks.
 * City leads so multi-office dropdowns are scannable by location first.
 * @param {Object} option - row with site_street_1, site_city, site_label
 * @returns {string}
 */
function siteStreetCityLine(option) {
  if (!option) return '';
  const street = (option.site_street_1 || '').trim();
  const city = (option.site_city || '').trim();
  const label = (option.site_label || '').trim();
  if (city && street) return `${city} · ${street}`;
  if (city) return city;
  if (street) return street;
  if (label) return label;
  return '';
}

/**
 * Single-line office from sites / provider_sites columns (street + city only; no state).
 *
 * @param {object} fields - site_street_1 or street_1, site_city or city, site_label or label
 * @returns {string}
 */
export function formatSiteAddressLine(fields) {
  if (!fields) return 'No site';
  const line = siteStreetCityLine({
    site_street_1: fields.site_street_1 ?? fields.street_1 ?? '',
    site_city: fields.site_city ?? fields.city ?? '',
    site_label: fields.site_label ?? fields.label ?? '',
  });
  return line || 'No site';
}

/**
 * Label for provider+site dropdowns and saved selections (PCP, pediatrician, referral L2, AT packet, etc.).
 * Format: Lastname, Firstname - Code (Street1, City) — no state. Omits empty parentheses when there is no site line.
 *
 * @param {Object} option - Provider+office option (provider_id, first_name, last_name, provider_type_code, payee_name, site_label, site_street_1, site_city).
 * @returns {string}
 */
export function formatProviderSiteOptionLabel(option) {
  if (!option) return '—';
  const base = formatProviderDisplayLabel(option);
  const siteLine = siteStreetCityLine(option);
  if (siteLine) return `${base} (${siteLine})`;
  return base;
}

/**
 * AT packet vendor search row: same as {@link formatProviderSiteOptionLabel} with type defaulting to AT.
 *
 * @param {Object} option - Same shape as formatProviderSiteOptionLabel
 * @returns {string}
 */
export function formatAtVendorSiteOptionLabel(option) {
  if (!option) return '—';
  const code = (option.provider_type_code ?? 'AT').trim() || 'AT';
  return formatProviderSiteOptionLabel({ ...option, provider_type_code: code });
}

/**
 * @deprecated Alias of {@link formatProviderSiteOptionLabel} (pipes removed for one standard format).
 */
export function formatProviderSitePipeLabel(option) {
  return formatProviderSiteOptionLabel(option);
}

/**
 * Pediatrician / PCP office line: street + city only (payee site), no state.
 */
export function formatPediatricianOfficeLabel(option) {
  if (!option) return '—';
  const street = (option.site_street_1 || '').trim();
  const city = (option.site_city || '').trim();
  if (city && street) return `${city} · ${street}`;
  if (city) return city;
  if (street) return street;
  const label = (option.site_label || '').trim();
  return label || 'Office on file';
}

/**
 * PCP line from GET /clients/:id pediatrician columns (same visual style as referral PCP search).
 */
export function formatPcpOptionLabelFromClient(client) {
  if (!client?.pediatrician_id) return '';
  const opt = {
    id: client.pediatrician_id,
    first_name: client.pediatrician_first_name,
    last_name: client.pediatrician_last_name,
    payee_name: client.pediatrician_payee_name,
    provider_type_code: 'DR',
    site_street_1: client.pediatrician_site_street_1,
    site_city: client.pediatrician_site_city,
    site_state: client.pediatrician_site_state,
    site_label: '',
    provider_id: client.pediatrician_id,
  };
  const label = formatProviderSiteOptionLabel(opt);
  if (label && label !== '—') return label;
  return '';
}

export function formatPcpOptionLabelFromReferral(referral) {
  if (!referral?.primary_care_physician_id) return '';
  const opt = {
    id: referral.primary_care_physician_id,
    first_name: referral.primary_care_physician_first_name,
    last_name: referral.primary_care_physician_last_name,
    payee_name: referral.primary_care_physician_payee_name,
    provider_type_code: referral.primary_care_physician_type_code,
    site_street_1: referral.primary_care_physician_site_street_1,
    site_city: referral.primary_care_physician_site_city,
    site_state: referral.primary_care_physician_site_state,
    site_label: referral.primary_care_physician_site_label,
    provider_id: referral.primary_care_physician_id,
  };
  const label = formatProviderSiteOptionLabel(opt);
  if (label && label !== '—') return label;
  return (referral.primary_care_physician_name || '').trim();
}

export function formatPediatricianPipeLabelFromClient(client) {
  if (!client?.pediatrician_id) return '';
  return formatProviderSitePipeLabel({
    first_name: client.pediatrician_first_name,
    last_name: client.pediatrician_last_name,
    payee_name: client.pediatrician_payee_name,
    provider_type_code: 'DR',
    site_street_1: client.pediatrician_site_street_1,
    site_city: client.pediatrician_site_city,
    site_state: client.pediatrician_site_state,
    provider_id: client.pediatrician_id,
  });
}

/** Optional specialist (DR + office) on client — same shape as pediatrician. */
export function formatSpecialistPipeLabelFromClient(client) {
  if (!client?.specialist_id) return '';
  return formatProviderSitePipeLabel({
    first_name: client.specialist_first_name,
    last_name: client.specialist_last_name,
    payee_name: client.specialist_payee_name,
    provider_type_code: 'DR',
    site_street_1: client.specialist_site_street_1,
    site_city: client.specialist_site_city,
    site_state: client.specialist_site_state,
    provider_id: client.specialist_id,
  });
}

export function formatProviderSiteLabelFromTeamServiceRow(s) {
  if (!s) return '';
  const opt = {
    first_name: s.provider_first_name,
    last_name: s.provider_last_name,
    provider_type_code: s.provider_type_code,
    payee_name: s.payee_name,
    site_label: s.provider_site_label,
    site_street_1: s.provider_site_street_1,
    site_city: s.provider_site_city,
    site_state: s.provider_site_state,
    provider_id: s.provider_id,
  };
  const label = formatProviderSiteOptionLabel(opt);
  return label && label !== '—' ? label : '';
}

/**
 * Contact lookup / selected row (client form): Last, First (Street1, City) — same shape as
 * {@link formatProviderSiteOptionLabel} when there is no type code (name, space, address in parentheses).
 * Uses contact.street_1 and contact.city (text on contacts).
 * When there is no street/city line, falls back to email (then phone) after an em dash for disambiguation only.
 *
 * @param {Object} contact - id, first_name, last_name, street_1, city, email, phone
 * @returns {string}
 */
export function formatContactOptionLabel(contact) {
  if (!contact) return '—';
  const first = (contact.first_name ?? '').trim();
  const last = (contact.last_name ?? '').trim();
  const name =
    [last, first].filter(Boolean).join(', ') ||
    (contact.id != null && contact.id !== '' ? `Contact #${contact.id}` : 'Contact');
  const street = (contact.street_1 ?? '').trim();
  const city = (contact.city ?? '').trim();
  let line = '';
  if (street && city) line = `${street}, ${city}`;
  else if (street) line = street;
  else if (city) line = city;
  if (line) return `${name} (${line})`;
  const email = (contact.email ?? '').trim();
  if (email) return `${name} — ${email}`;
  const phone = (contact.phone ?? '').trim();
  if (phone) return `${name} — ${phone}`;
  return name;
}
